import React, { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import { ICarLocationParam } from '@share/api';
import {
  CrossSellOrigin,
  ICrossSellTile,
} from '@share/common-types';
import {
  buildCrossSellTilesFromEntries,
  getAccountPathFromLocation,
  GetHomeParams,
  resolveActivitiesBaseUrl,
  AppDispatch,
  RootState,
} from '@share/utils';
import { crossSellKey, ICrossSellState, prefetchCrossSell } from '@share/store/slices';

import hotelPlaceholder from '@assets/images/cross-sell/hotel.svg';
import hotelPhoto from '@assets/images/cross-sell/hotel-photo.jpg';
import condoPlaceholder from '@assets/images/cross-sell/condo.svg';
import condoPhoto from '@assets/images/cross-sell/condo-photo.jpg';
import carPlaceholder from '@assets/images/cross-sell/car.svg';
import carCityPhoto from '@assets/images/cross-sell/car-city-photo.jpg';
import carAirportPhoto from '@assets/images/cross-sell/car-airport-photo.jpg';
import activityPlaceholder from '@assets/images/cross-sell/activity.svg';
import activityPhoto from '@assets/images/cross-sell/activity-photo.jpg';

import './style.scss';

interface ICrossSellSectionProps {
  origin: CrossSellOrigin;
  /** Hotel / condo bookings. */
  propertyId?: string | number;
  /** Car bookings (pick-up / drop-off locations). */
  carLocations?: ICarLocationParam[];
  checkIn?: string;
  checkOut?: string;
  currency?: string;
}

// Effective per-section cap (overrides DEFAULT_MAX_TILES). 4 for consistency with the
// activities front; here no origin offers more than 3 verticals. TODO(TRAV23-4194): from account prefs.
const MAX_TILES = 4;

const productLabelId: Record<ICrossSellTile['product'], string> = {
  hotel: 'crossSell.product.hotel',
  condo: 'crossSell.product.condo',
  carCity: 'crossSell.product.carCity',
  carAirport: 'crossSell.product.carAirport',
  activity: 'crossSell.product.activity',
};

/**
 * Thumbnail shown on each tile, per offered product. All are bundled assets; the
 * matching `PRODUCT_IMAGE` vector shows only if one of these fails to load.
 */
const PRODUCT_PHOTO: Record<ICrossSellTile['product'], string> = {
  hotel: hotelPhoto,
  condo: condoPhoto,
  carCity: carCityPhoto,
  carAirport: carAirportPhoto,
  activity: activityPhoto,
};

/** Vector fallback shown behind the photo if it fails to load, per offered product. */
const PRODUCT_IMAGE: Record<ICrossSellTile['product'], string> = {
  hotel: hotelPlaceholder,
  condo: condoPlaceholder,
  carCity: carPlaceholder,
  carAirport: carPlaceholder,
  activity: activityPlaceholder,
};

/**
 * Offers are grouped by product family rather than by exact product: the two car
 * verticals (city and airport) sit under one "Rent a car" heading, and the row titles
 * tell them apart.
 */
type CrossSellFamily = 'hotel' | 'condo' | 'car' | 'activity';

const FAMILY: Record<ICrossSellTile['product'], CrossSellFamily> = {
  hotel: 'hotel',
  condo: 'condo',
  carCity: 'car',
  carAirport: 'car',
  activity: 'activity',
};

/** Heading above each family's rows. */
const GROUP_TITLE: Record<CrossSellFamily, { id: string; defaultMessage: string }> = {
  hotel: { id: 'crossSell.group.hotel', defaultMessage: 'Book a hotel' },
  condo: { id: 'crossSell.group.condo', defaultMessage: 'Book a condo' },
  car: { id: 'crossSell.group.car', defaultMessage: 'Rent a car' },
  activity: { id: 'crossSell.group.activity', defaultMessage: 'Book things to do' },
};

/** Right-hand side of the family heading: how much the backend search found. */
const GROUP_COUNT: Record<CrossSellFamily, { id: string; defaultMessage: string }> = {
  // "nearby", not "available": the count comes from a search around the booked place, so
  // it reflects what is close by rather than the destination's whole inventory.
  hotel: { id: 'crossSell.count.hotel', defaultMessage: '{count}+ hotels nearby' },
  condo: { id: 'crossSell.count.condo', defaultMessage: '{count}+ condos nearby' },
  car: { id: 'crossSell.count.car', defaultMessage: '{count}+ cars available' },
  activity: { id: 'crossSell.count.activity', defaultMessage: '{count}+ experiences' },
};

/** Row title - leads with what would be booked, not with the place. */
const TILE_TITLE: Record<ICrossSellTile['product'], { id: string; defaultMessage: string }> = {
  hotel: { id: 'crossSell.tile.hotel', defaultMessage: 'Stay in {place}' },
  condo: { id: 'crossSell.tile.condo', defaultMessage: 'Condo stay in {place}' },
  carCity: { id: 'crossSell.tile.carCity', defaultMessage: 'Pick up in {place}' },
  carAirport: { id: 'crossSell.tile.carAirport', defaultMessage: 'Pick up at {place}' },
  activity: { id: 'crossSell.tile.activity', defaultMessage: 'Tours & tickets in {place}' },
};

/**
 * The place name without its administrative tail: the API returns "Miami, FL, United
 * States" where a row only has room for the leading part. The full string stays as the
 * link's tooltip.
 */
const shortPlace = (tile: ICrossSellTile): string => (tile.title.split(',')[0] || tile.title).trim();

/** "Add {this}" - the offered family with article, e.g. "a car rental". */
const OFFER_PHRASE: Record<CrossSellFamily, { id: string; defaultMessage: string }> = {
  hotel: { id: 'crossSell.offer.hotel', defaultMessage: 'a hotel' },
  condo: { id: 'crossSell.offer.condo', defaultMessage: 'a condo' },
  car: { id: 'crossSell.offer.car', defaultMessage: 'a car rental' },
  activity: { id: 'crossSell.offer.activity', defaultMessage: 'an activity' },
};

/** "...to your {this} reservation" — the booked product type. */
const PRODUCT_TYPE_PHRASE: Record<CrossSellOrigin, { id: string; defaultMessage: string }> = {
  hotel: { id: 'crossSell.productType.hotel', defaultMessage: 'hotel' },
  condo: { id: 'crossSell.productType.condo', defaultMessage: 'condo' },
  car: { id: 'crossSell.productType.car', defaultMessage: 'car rental' },
  activity: { id: 'crossSell.productType.activity', defaultMessage: 'activity' },
};

/** Lead of the row's second line, before the distance - keyed by what was booked. */
const FROM_ORIGIN_PHRASE: Record<CrossSellOrigin, { id: string; defaultMessage: string }> = {
  hotel: { id: 'crossSell.from.hotel', defaultMessage: 'From your hotel' },
  condo: { id: 'crossSell.from.condo', defaultMessage: 'From your condo' },
  car: { id: 'crossSell.from.car', defaultMessage: 'From your pick-up' },
  activity: { id: 'crossSell.from.activity', defaultMessage: 'From your activity' },
};

/** Airport pick-ups read better against the terminal than against the booked product. */
const TERMINAL_PHRASE = { id: 'crossSell.atTerminal', defaultMessage: 'At the terminal' };

export const CrossSellSection: React.FC<ICrossSellSectionProps> = ({
  origin,
  propertyId,
  carLocations,
  checkIn,
  checkOut,
  currency,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const account = useSelector((state: RootState) => state.loginStore.account);
  const homeUrl = useSelector((state: RootState) => state.navigationMenuStore?.items?.homeUrl);
  const crossSellStore = useSelector((state: RootState) => state.crossSellStore) as ICrossSellState | undefined;
  const { key, entries } = crossSellStore ?? { key: null, entries: null };

  const carLocationsKey = useMemo(() => JSON.stringify(carLocations ?? []), [carLocations]);

  // The recommendations are prefetched earlier in the flow (hotel/condo at
  // recheck time, cars on "reserve"), so by the time this confirmation screen
  // mounts the result is usually cached already. This dispatch is a self-healing
  // fallback: the thunk is de-duped by request key, so it only fires when the
  // cache is missing. Whether cross-sell is enabled is decided by the backend
  // (a disabled vertical simply returns nothing), so there's no front gating.
  useEffect(() => {
    dispatch(prefetchCrossSell({ origin, propertyId, carLocations, checkIn, checkOut }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, propertyId, carLocationsKey, checkIn, checkOut, dispatch]);

  const tiles = useMemo<ICrossSellTile[]>(() => {
    // Only use the cached result when it matches this booking's request.
    if (!entries) return [];
    if (key !== crossSellKey({ origin, propertyId, carLocations, checkIn, checkOut })) return [];
    return buildCrossSellTilesFromEntries(origin, entries, {
      accountPath: getAccountPathFromLocation(),
      homeParams: GetHomeParams(account),
      sameOriginProducts: ['hotel', 'condo', 'carCity', 'carAirport'],
      hosts: { activity: resolveActivitiesBaseUrl(account, homeUrl) },
      currency,
    }, MAX_TILES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, propertyId, carLocationsKey, checkIn, checkOut, entries, key, account, homeUrl, currency]);

  if (!tiles.length) return null;

  const destination = tiles[0].destination || tiles[0].title;

  // Group the tiles by product family, preserving first-seen order.
  const groups: Array<[CrossSellFamily, ICrossSellTile[]]> = [];
  const groupIndex = new Map<CrossSellFamily, number>();
  for (const tile of tiles) {
    const family = FAMILY[tile.product];
    let index = groupIndex.get(family);
    if (index == null) {
      index = groups.length;
      groupIndex.set(family, index);
      groups.push([family, []]);
    }
    groups[index][1].push(tile);
  }
  // 2+ families -> centered title; a single one names the product in the heading instead.
  const isMulti = groups.length >= 2;

  const offerPhrase = (family: CrossSellFamily) => intl.formatMessage(OFFER_PHRASE[family]);

  /**
   * The count beside a family heading. Every row carries its own `resultCount` for its own
   * location, so the family shows the largest - the most any single pick-up point or place
   * offers - rather than a sum across searches that overlap.
   */
  const groupCount = (familyTiles: ICrossSellTile[]): number | undefined => {
    const counts = familyTiles
      .map((tile) => tile.resultCount)
      .filter((count): count is number => count != null && count > 0);
    return counts.length ? Math.max(...counts) : undefined;
  };

  // "From your hotel · 0.3 mi" - the lead says how the row relates to the booking; the
  // distance is appended only when the API sent one. The result count is no longer here:
  // it belongs to the family heading, which covers every row beneath it.
  const renderStats = (tile: ICrossSellTile) => {
    const lead = tile.product === 'carAirport' ? TERMINAL_PHRASE : FROM_ORIGIN_PHRASE[origin];
    return (
      <span className="cross-sell-tile__stats">
        <FormattedMessage {...lead} />
        {tile.distanceMiles != null && (
          <>
            {' · '}
            <FormattedMessage
              id="crossSell.miles"
              defaultMessage="{miles} mi"
              values={{ miles: intl.formatNumber(tile.distanceMiles, { maximumFractionDigits: 1 }) }}
            />
          </>
        )}
      </span>
    );
  };

  const renderTile = (tile: ICrossSellTile) => (
    // Opened in a new tab so the guest keeps the confirmation page they are booking from:
    // starting a second booking in the same tab loses the reference to the one just made.
    <a
      key={tile.url}
      className="cross-sell-tile"
      href={tile.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span
        className="cross-sell-tile__image"
        style={{ backgroundImage: `url("${PRODUCT_PHOTO[tile.product]}"), url("${PRODUCT_IMAGE[tile.product]}")` }}
        aria-hidden="true"
      />
      <span className="cross-sell-tile__info">
        {/* Leads with the action and the short place name. The full place string is already
            in the section heading, so the row does not repeat it - it stays as the tooltip. */}
        <span className="cross-sell-tile__title" title={tile.destination || tile.title}>
          <FormattedMessage {...TILE_TITLE[tile.product]} values={{ place: shortPlace(tile) }} />
        </span>
        {renderStats(tile)}
        <span className="cross-sell-tile__meta">
          <span className={`cross-sell-tile__badge cross-sell-tile__badge--${tile.product}`}>
            <FormattedMessage id={productLabelId[tile.product]} defaultMessage={tile.product} />
          </span>
          {tile.startingPrice != null && (
            <span className="cross-sell-tile__price">
              <FormattedMessage id="crossSell.from" defaultMessage="from" />{' '}
              {tile.currency ? `${tile.currency} ` : ''}
              {tile.startingPrice}
            </span>
          )}
        </span>
      </span>
      <span className="cross-sell-tile__cta">
        <FormattedMessage id="crossSell.cta" defaultMessage="Book now" />
      </span>
    </a>
  );

  return (
    <div
      className={`cross-sell-section${isMulti ? ' cross-sell-section--multi' : ''}${
        origin === 'car' ? ' cross-sell-section--car' : ''
      }`}
    >
      {isMulti ? (
        <p className="cross-sell-section__title cross-sell-section__title--center">
          <FormattedMessage
            id="crossSell.section.multiTitle"
            defaultMessage="Make the most of your reservation @ {destination}"
            values={{ destination }}
          />
        </p>
      ) : (
        <div className="cross-sell-section__heading">
          <p className="cross-sell-section__eyebrow">
            <FormattedMessage id="crossSell.section.makeItATrip" defaultMessage="Make it a trip" />
          </p>
          <p className="cross-sell-section__title">
            <FormattedMessage
              id="crossSell.section.addSingle"
              defaultMessage="Add {product} to your {productType} reservation @ {destination}"
              values={{
                product: offerPhrase(groups[0][0]),
                productType: intl.formatMessage(PRODUCT_TYPE_PHRASE[origin]),
                destination,
              }}
            />
          </p>
        </div>
      )}
      {/* One block per family: a heading naming the product and what the search found,
          then that family's rows. */}
      {groups.map(([family, familyTiles]) => {
        const count = groupCount(familyTiles);
        return (
          <div className="cross-sell-group" key={family}>
            <div className="cross-sell-group__header">
              <p className="cross-sell-group__title">
                <FormattedMessage {...GROUP_TITLE[family]} />
              </p>
              {count != null && (
                <span className="cross-sell-group__count">
                  <FormattedMessage {...GROUP_COUNT[family]} values={{ count }} />
                </span>
              )}
            </div>
            <div className="cross-sell-section__tiles">{familyTiles.map(renderTile)}</div>
          </div>
        );
      })}
    </div>
  );
};
